<script setup lang="ts">
  import { useModal } from 'vue-final-modal';
  import {
    ColoredButton,
    DefaultTable,
    DocumentRevisionStatusDot,
    FieldSearchBar,
    HeaderTableWithButtons,
    NoDocuments,
    Pagination,
    SearchNoResults,
    SearchToolbar,
    SelectInput,
    TableHeader,
    TableLoading,
  } from '@/shared/ui';
  import type { FilterOptions } from '@/entities/Document';
  import { isExternalDocument, type DocumentRevision } from '@/entities/Document';
  import type { FilteredListTableEmits } from '@/shared/lib';
  import { formatDocumentPrefix, formatDocumentTitle, OrderOptions, SEARCH_TYPE_FIELD_OPTIONS } from '@/shared/lib';
  import { ListingActions as ListagemActions } from '@/widgets/document-actions';
  import { QUANTITY_OPTIONS } from '@/shared/config';
  import NoFilesIcon from '@/shared/assets/images/no-documents.svg';
  import QualityRecordKebabDropdown from './quality-records/QualityRecordKebabDropdown.vue';
  import registrationPaper from '@/shared/assets/icons/RegistrationPaper.svg';
  import { Can } from '@/entities/Permission';
  import { MasterListModal } from '@/features/Document';

  const { open: openMasterList, close: closeMasterList } = useModal({
    component: MasterListModal,
    attrs: {
      title: 'Lista Mestra',
      onClose: () => closeMasterList(),
    },
  });

  const props = withDefaults(
    defineProps<{
      documents: DocumentRevision[];
      loading: boolean;
      title: string;
      totalItems: number;
      currentPage: number;
      itemsPerPage: number;
      orderOptions?: OrderOptions[];
      isFilterActive?: boolean;
      searchTerm?: string;
      shouldShowNoResults?: boolean;
      shouldShowFilterNoResults?: boolean;
      shouldShowEmptyState?: boolean;
      hasResults?: boolean;
    }>(),
    {
      shouldShowNoResults: false,
      shouldShowFilterNoResults: false,
      shouldShowEmptyState: false,
      hasResults: false,
    }
  );

  const emit = defineEmits<FilteredListTableEmits<FilterOptions>>();

  const searchFieldId = defineModel<string>('searchFieldId', { default: 'prefix' });

  const handleSearch = (queryString: string) => {
    emit('search', queryString);
  };

  const handleSort = (sort: string, order: string) => {
    emit('update:sort', sort, order);
  };

  const handleFilter = (filters: FilterOptions) => {
    emit('update:filter', filters);
  };

  const handleAction = (action: string) => {
    emit('action', action);
  };

  const handlePageChange = (page: number) => {
    emit('page-changed', page);
  };

  const handleQuantityChange = (quantity: number) => {
    emit('quantity-changed', quantity);
  };

  const handleFilterClicked = () => {
    emit('filter-clicked');
  };

  const getDocumentPrefix = (document: DocumentRevision): string => {
    return formatDocumentPrefix(document.document?.prefix);
  };

  const getDocumentTitle = (document: DocumentRevision): string => {
    return formatDocumentTitle(document.document?.name);
  };
</script>

<template>
  <div class="w-full">
    <Can :permissions="[{ action: 'createMasterList', subject: 'Document' }]">
      <div
        class="mb-4"
        data-testid="master-list-button"
      >
        <ColoredButton
          text="Lista Mestra"
          :icon="registrationPaper"
          icon-alt="Lista Mestra"
          @click="openMasterList"
        />
      </div>
    </Can>

    <SearchToolbar class="mb-6">
      <template #search>
        <div
          class="w-full"
          data-testid="filtered-list-search"
        >
          <FieldSearchBar
            v-model:search-field-id="searchFieldId"
            independent-search-field
            :clear-search-on-field-change="false"
            :search-fields="SEARCH_TYPE_FIELD_OPTIONS"
            :initial-search-term="props.searchTerm ?? ''"
            @search="handleSearch"
          />
        </div>
      </template>
      <template
        v-if="!props.loading"
        #quantity
      >
        <SelectInput
          data-testid="filtered-list-quantity-select"
          :model-value="itemsPerPage"
          :options="QUANTITY_OPTIONS"
          class="cursor-pointer"
          @update:model-value="handleQuantityChange"
        />
      </template>
    </SearchToolbar>

    <div
      v-if="props.loading"
      class="w-full"
    >
      <TableLoading :row-size="10" />
    </div>

    <template v-else>
      <DefaultTable
        table-class="w-full table-fixed"
        class="divide-y divide-gray-02 min-w-full"
      >
        <colgroup>
          <col>
          <col>
          <col>
          <col>
        </colgroup>
        <thead class="bg-white-01 divide-y divide-gray-02">
          <tr>
            <HeaderTableWithButtons
              id="header-table-with-buttons"
              colspan="4"
              :text="title"
              :order-options="orderOptions"
              :is-filter-active="isFilterActive"
              :action-modal="QualityRecordKebabDropdown"
              @update:sort="handleSort"
              @update:filter="handleFilter"
              @filter-clicked="handleFilterClicked"
              @action="handleAction"
            />
          </tr>
          <tr v-if="!shouldShowFilterNoResults && !shouldShowEmptyState">
            <TableHeader
              text="Prefixo"
              class="text-xs/5"
            />
            <TableHeader
              text="Título"
              class="text-xs/5"
            />
            <TableHeader
              text="Status"
              class="text-xs/5"
            />
            <th
              class="pl-4 pr-6 py-4.5 align-middle"
              data-testid="filtered-list-actions-header"
            >
              <div class="flex justify-end">
                <div class="flex w-[7.5rem]">
                  <span class="text-xs/5 font-semibold text-gray-07 whitespace-nowrap">Ações</span>
                </div>
              </div>
            </th>
          </tr>
        </thead>
        <tbody class="bg-white-01 divide-y divide-gray-02 border-t border-gray-02">
          <tr v-if="props.shouldShowFilterNoResults">
            <td
              colspan="4"
              class="py-8"
            >
              <SearchNoResults />
            </td>
          </tr>
          <tr v-else-if="props.shouldShowEmptyState">
            <td
              colspan="4"
              class="py-8"
            >
              <NoDocuments
                title="Ops! Parece que a pasta ainda não contém documentos"
                :icon="NoFilesIcon"
              />
            </td>
          </tr>
          <template v-else>
            <tr
              v-for="document in documents"
              :key="document.id"
              class="table-row-hover border-t border-gray-02 transition-colors duration-300 ease-out"
            >
              <td class="pl-6 py-4.5 text-sm text-gray-07">
                {{ getDocumentPrefix(document) }}
              </td>

              <td class="pl-6 py-4.5 pr-0">
                <div class="flex min-w-0 items-center gap-2">
                  <span class="truncate text-sm text-gray-07">
                    {{ getDocumentTitle(document) }}
                  </span>
                  <span
                    v-if="isExternalDocument(document)"
                    class="inline-flex shrink-0 items-center rounded px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    Externo
                  </span>
                </div>
              </td>

              <td
                class="pl-6 py-4.5"
                data-testid="filtered-list-status-cell"
              >
                <div class="flex items-center">
                  <DocumentRevisionStatusDot :status="document.status" />
                </div>
              </td>

              <td
                class="pl-4 pr-6 py-4.5 text-sm text-gray-07"
                data-testid="filtered-list-actions-cell"
              >
                <div class="flex justify-end">
                  <div class="flex w-[7.5rem]">
                    <ListagemActions :document="document" />
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </DefaultTable>

      <div class="mt-3">
        <Pagination
          :total-items="totalItems"
          :current-page="currentPage"
          :items-per-page="itemsPerPage"
          @page-changed="handlePageChange"
        />
      </div>
    </template>
  </div>
</template>
